import { useState, useMemo } from 'react';
import { AlertTriangle, Bell, Building, FileText, Settings, Search, Filter as FilterIcon, X, MessageSquare, Clock, User, MapPin, CheckCircle2, AlertCircle, ChevronRight, Plus } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const mockIncidentsES = [
  { id: 'INC-1041', titulo: 'Bombilla portal fundida', comunidad: 'C/ del Mar 14', vivienda: 'Portal', categoria: 'Electricidad', estado: 'abierta', urgencia: 'media', fecha: 'hace 2 h', autor: 'María L.', desc: 'La luz del portal no funciona desde anoche.' },
  { id: 'INC-1040', titulo: 'Fuga de agua en el 3º A', comunidad: 'Av. Diagonal 245', vivienda: '3º A', categoria: 'Fontanería', estado: 'en_curso', urgencia: 'alta', fecha: 'hace 5 h', autor: 'Joan M.', desc: 'Mancha de humedad en el techo. Se filtra agua.' },
  { id: 'INC-1039', titulo: 'Puerta garaje no abre', comunidad: 'C/ Pelayo 8', vivienda: 'Zonas comunes', categoria: 'Cerrajería', estado: 'en_curso', urgencia: 'alta', fecha: 'ayer', autor: 'Andrea P.', desc: 'El mando no responde. Tres vecinos atrapados fuera.' },
  { id: 'INC-1038', titulo: 'Limpieza escalera', comunidad: 'C/ del Mar 14', vivienda: 'Escalera A', categoria: 'Limpieza', estado: 'resuelta', urgencia: 'baja', fecha: 'hace 2 días', autor: 'Carlos R.', desc: 'No se ha limpiado esta semana.' },
  { id: 'INC-1037', titulo: 'Ascensor con ruido', comunidad: 'Av. Diagonal 245', vivienda: 'Ascensor', categoria: 'Estructural', estado: 'abierta', urgencia: 'media', fecha: 'hace 2 días', autor: 'Elena V.', desc: 'Ruido fuerte al frenar entre pisos.' },
  { id: 'INC-1036', titulo: 'Grifo cocina goteando', comunidad: 'C/ Pelayo 8', vivienda: '1º B', categoria: 'Fontanería', estado: 'resuelta', urgencia: 'baja', fecha: 'hace 3 días', autor: 'Pere S.', desc: 'Goteo constante.' },
];

const mockIncidentsCA = [
  { id: 'INC-1041', titulo: 'Bombeta portal fosa', comunidad: 'C/ del Mar 14', vivienda: 'Portal', categoria: 'Electricitat', estado: 'abierta', urgencia: 'media', fecha: 'fa 2 h', autor: 'Maria L.', desc: 'La llum del portal no funciona des d’ahir.' },
  { id: 'INC-1040', titulo: 'Fuga d’aigua al 3r 1a', comunidad: 'Av. Diagonal 245', vivienda: '3r 1a', categoria: 'Lampisteria', estado: 'en_curso', urgencia: 'alta', fecha: 'fa 5 h', autor: 'Joan M.', desc: 'Taca d’humitat al sostre.' },
  { id: 'INC-1039', titulo: 'Porta garatge no obre', comunidad: 'C/ Pelayo 8', vivienda: 'Zones comunes', categoria: 'Serralleria', estado: 'en_curso', urgencia: 'alta', fecha: 'ahir', autor: 'Andrea P.', desc: 'El comandament no respon.' },
  { id: 'INC-1038', titulo: 'Neteja escala', comunidad: 'C/ del Mar 14', vivienda: 'Escala A', categoria: 'Neteja', estado: 'resuelta', urgencia: 'baja', fecha: 'fa 2 dies', autor: 'Carles R.', desc: 'No s’ha netejat aquesta setmana.' },
  { id: 'INC-1037', titulo: 'Ascensor amb soroll', comunidad: 'Av. Diagonal 245', vivienda: 'Ascensor', categoria: 'Estructural', estado: 'abierta', urgencia: 'media', fecha: 'fa 2 dies', autor: 'Elena V.', desc: 'Soroll fort en frenar.' },
  { id: 'INC-1036', titulo: 'Aixeta cuina degotant', comunidad: 'C/ Pelayo 8', vivienda: '1r 2a', categoria: 'Lampisteria', estado: 'resuelta', urgencia: 'baja', fecha: 'fa 3 dies', autor: 'Pere S.', desc: 'Degoteig constant.' },
];

export default function AdminFlow() {
  const { lang } = useLanguage();
  const isCA = lang === 'ca';
  const incidents = isCA ? mockIncidentsCA : mockIncidentsES;
  const L = isCA
    ? {
        title: 'Panell d’administrador',
        sub: 'Gestiona totes les comunitats des d’un sol lloc.',
        searchPh: 'Cercar incidència, comunitat, veí…',
        filterAll: 'Totes',
        filterOpen: 'Obertes',
        filterInProgress: 'En curs',
        filterResolved: 'Resoltes',
        nuevaAviso: 'Nou avís',
        kpiOpen: 'Obertes',
        kpiUrgent: 'Urgents',
        kpiInProgress: 'En curs',
        kpiResolved: 'Resoltes (mes)',
        navIncidents: 'Incidències',
        navAvisos: 'Avisos',
        navComm: 'Comunitats',
        navDocs: 'Documents',
        navSettings: 'Configuració',
        col: { id: 'Ref', title: 'Incidència', community: 'Comunitat', status: 'Estat', urgency: 'Urgència', date: 'Data' },
        states: { abierta: 'Oberta', en_curso: 'En curs', resuelta: 'Resolta' },
        urgencies: { alta: 'Alta', media: 'Mitja', baja: 'Baixa' },
        details: 'Detalls',
        close: 'Tancar',
        history: 'Històric',
        comments: 'Comentaris',
        addComment: 'Afegir comentari…',
        send: 'Enviar',
        changeStatus: 'Canviar estat',
        assignTo: 'Assignar a',
        techs: ['Pere (Lampista)', 'Anna (Electricista)', 'Joan (Manteniment)'],
        emptyDetail: 'Selecciona una incidència per veure els detalls',
        historyItems: [
          { who: 'Sistema', what: 'Incidència creada', when: 'fa 2 h' },
          { who: 'Marc (admin)', what: 'Estat canviat a En curs', when: 'fa 1 h' },
          { who: 'Pere (Lampista)', what: 'Comentari afegit', when: 'fa 20 min' },
        ],
      }
    : {
        title: 'Panel de administrador',
        sub: 'Gestiona todas las comunidades desde un único sitio.',
        searchPh: 'Buscar incidencia, comunidad, vecino…',
        filterAll: 'Todas',
        filterOpen: 'Abiertas',
        filterInProgress: 'En curso',
        filterResolved: 'Resueltas',
        nuevaAviso: 'Nuevo aviso',
        kpiOpen: 'Abiertas',
        kpiUrgent: 'Urgentes',
        kpiInProgress: 'En curso',
        kpiResolved: 'Resueltas (mes)',
        navIncidents: 'Incidencias',
        navAvisos: 'Avisos',
        navComm: 'Comunidades',
        navDocs: 'Documentos',
        navSettings: 'Configuración',
        col: { id: 'Ref', title: 'Incidencia', community: 'Comunidad', status: 'Estado', urgency: 'Urgencia', date: 'Fecha' },
        states: { abierta: 'Abierta', en_curso: 'En curso', resuelta: 'Resuelta' },
        urgencies: { alta: 'Alta', media: 'Media', baja: 'Baja' },
        details: 'Detalles',
        close: 'Cerrar',
        history: 'Histórico',
        comments: 'Comentarios',
        addComment: 'Añadir comentario…',
        send: 'Enviar',
        changeStatus: 'Cambiar estado',
        assignTo: 'Asignar a',
        techs: ['Pedro (Fontanero)', 'Ana (Electricista)', 'Juan (Mantenimiento)'],
        emptyDetail: 'Selecciona una incidencia para ver los detalles',
        historyItems: [
          { who: 'Sistema', what: 'Incidencia creada', when: 'hace 2 h' },
          { who: 'Marcos (admin)', what: 'Estado cambiado a En curso', when: 'hace 1 h' },
          { who: 'Pedro (Fontanero)', what: 'Comentario añadido', when: 'hace 20 min' },
        ],
      };

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [statusMap, setStatusMap] = useState(() =>
    Object.fromEntries(incidents.map((i) => [i.id, i.estado]))
  );

  const filtered = useMemo(() => {
    return incidents.filter((i) => {
      const st = statusMap[i.id];
      if (filter === 'open' && st !== 'abierta') return false;
      if (filter === 'in_progress' && st !== 'en_curso') return false;
      if (filter === 'resolved' && st !== 'resuelta') return false;
      if (search) {
        const s = search.toLowerCase();
        return (
          i.titulo.toLowerCase().includes(s) ||
          i.comunidad.toLowerCase().includes(s) ||
          i.autor.toLowerCase().includes(s) ||
          i.id.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [filter, search, incidents, statusMap]);

  const counts = useMemo(
    () => ({
      open: incidents.filter((i) => statusMap[i.id] === 'abierta').length,
      urgent: incidents.filter((i) => i.urgencia === 'alta' && statusMap[i.id] !== 'resuelta').length,
      in_progress: incidents.filter((i) => statusMap[i.id] === 'en_curso').length,
      resolved: incidents.filter((i) => statusMap[i.id] === 'resuelta').length,
    }),
    [incidents, statusMap]
  );

  const selected = incidents.find((i) => i.id === selectedId);

  return (
    <div className="bg-app min-h-[calc(100vh-73px)] flex" data-testid="admin-flow">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 border-r border-app bg-surface min-h-[calc(100vh-73px)]">
        <div className="p-6">
          <div className="label-eyebrow mb-4">Despacho Demo</div>
          <nav className="space-y-1">
            <NavItem icon={AlertTriangle} label={L.navIncidents} active count={counts.open + counts.in_progress} />
            <NavItem icon={Bell} label={L.navAvisos} count={2} />
            <NavItem icon={Building} label={L.navComm} count={3} />
            <NavItem icon={FileText} label={L.navDocs} />
            <NavItem icon={Settings} label={L.navSettings} />
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-app">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-secondary-app text-white flex items-center justify-center text-sm font-medium">
              MC
            </div>
            <div className="text-xs">
              <div className="text-app font-medium">Marcos C.</div>
              <div className="text-app-muted">Admin</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="p-6 md:p-8 border-b border-app">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl text-app">{L.title}</h1>
              <p className="text-sm text-app-secondary mt-1">{L.sub}</p>
            </div>
            <button className="inline-flex items-center gap-2 bg-secondary-app text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#495845] transition-colors">
              <Plus size={15} /> {L.nuevaAviso}
            </button>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <Kpi label={L.kpiOpen} value={counts.open} tone="warning" />
            <Kpi label={L.kpiUrgent} value={counts.urgent} tone="destructive" />
            <Kpi label={L.kpiInProgress} value={counts.in_progress} tone="info" />
            <Kpi label={L.kpiResolved} value={counts.resolved} tone="success" />
          </div>
        </div>

        <div className="p-6 md:p-8">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-app-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={L.searchPh}
                className="w-full pl-9 pr-3 py-2.5 bg-surface border border-app rounded-md text-sm focus:outline-none focus:border-primary-app focus:ring-2 focus:ring-primary-app/10"
                data-testid="admin-search"
              />
            </div>
            <div className="flex items-center gap-1.5 bg-surface border border-app rounded-md p-1">
              {[
                ['all', L.filterAll],
                ['open', L.filterOpen],
                ['in_progress', L.filterInProgress],
                ['resolved', L.filterResolved],
              ].map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    filter === k
                      ? 'bg-primary-app text-white'
                      : 'text-app-secondary hover:text-app'
                  }`}
                  data-testid={`admin-filter-${k}`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="bg-surface border border-app rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface-muted border-b border-app">
                <tr>
                  <Th>{L.col.id}</Th>
                  <Th>{L.col.title}</Th>
                  <Th className="hidden md:table-cell">{L.col.community}</Th>
                  <Th>{L.col.status}</Th>
                  <Th className="hidden lg:table-cell">{L.col.urgency}</Th>
                  <Th className="hidden md:table-cell">{L.col.date}</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr
                    key={i.id}
                    onClick={() => setSelectedId(i.id)}
                    className="border-b border-app last:border-0 hover:bg-surface-muted cursor-pointer transition-colors"
                    data-testid={`admin-row-${i.id}`}
                  >
                    <Td className="font-mono text-xs text-app-muted">{i.id}</Td>
                    <Td>
                      <div className="font-medium text-app">{i.titulo}</div>
                      <div className="text-xs text-app-muted mt-0.5">
                        {i.vivienda} · {i.categoria}
                      </div>
                    </Td>
                    <Td className="hidden md:table-cell text-sm text-app-secondary">{i.comunidad}</Td>
                    <Td>
                      <StatusBadge status={statusMap[i.id]} labels={L.states} />
                    </Td>
                    <Td className="hidden lg:table-cell">
                      <UrgencyBadge urg={i.urgencia} labels={L.urgencies} />
                    </Td>
                    <Td className="hidden md:table-cell text-xs text-app-muted">{i.fecha}</Td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-app-muted text-sm">
                      —
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Side detail panel */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/30 flex justify-end"
          onClick={() => setSelectedId(null)}
          data-testid="admin-detail-overlay"
        >
          <div
            className="w-full md:w-[480px] bg-app border-l border-app overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-app border-b border-app p-5 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-xs text-app-muted">{selected.id}</div>
                <h2 className="font-serif text-xl text-app mt-0.5">{selected.titulo}</h2>
              </div>
              <button
                onClick={() => setSelectedId(null)}
                className="p-1.5 hover:bg-surface-muted rounded-md"
                data-testid="admin-detail-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={statusMap[selected.id]} labels={L.states} />
                <UrgencyBadge urg={selected.urgencia} labels={L.urgencies} />
              </div>

              <Detail icon={MapPin} label={L.col.community} value={`${selected.comunidad} · ${selected.vivienda}`} />
              <Detail icon={User} label={isCA ? 'Reportat per' : 'Reportado por'} value={selected.autor} />
              <Detail icon={Clock} label={L.col.date} value={selected.fecha} />

              <div>
                <div className="label-eyebrow mb-2">{isCA ? 'Descripció' : 'Descripción'}</div>
                <p className="text-sm text-app-secondary bg-surface border border-app rounded-md p-3">
                  {selected.desc}
                </p>
              </div>

              <div>
                <div className="label-eyebrow mb-2">{L.changeStatus}</div>
                <div className="flex gap-2">
                  {['abierta', 'en_curso', 'resuelta'].map((st) => (
                    <button
                      key={st}
                      onClick={() => setStatusMap({ ...statusMap, [selected.id]: st })}
                      className={`flex-1 text-xs font-medium px-3 py-2 rounded-md border transition-colors ${
                        statusMap[selected.id] === st
                          ? 'bg-primary-app text-white border-primary-app'
                          : 'bg-surface border-app text-app-secondary hover:border-primary-app/40'
                      }`}
                      data-testid={`admin-status-${st}`}
                    >
                      {L.states[st]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="label-eyebrow mb-2">{L.assignTo}</div>
                <select className="w-full bg-surface border border-app rounded-md px-3 py-2.5 text-sm">
                  {L.techs.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="label-eyebrow mb-2">{L.history}</div>
                <ul className="space-y-3">
                  {L.historyItems.map((h, idx) => (
                    <li key={idx} className="flex gap-3 text-xs">
                      <div className="w-1.5 h-1.5 mt-1.5 bg-primary-app rounded-full flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-app font-medium">{h.who}</span>
                        <span className="text-app-secondary"> · {h.what}</span>
                        <div className="text-app-muted mt-0.5">{h.when}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="label-eyebrow mb-2">{L.comments}</div>
                <div className="flex gap-2">
                  <input
                    placeholder={L.addComment}
                    className="flex-1 bg-surface border border-app rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-app"
                  />
                  <button className="bg-primary-app text-white text-sm font-medium px-4 rounded-md hover:bg-[#8F3E2E]">
                    {L.send}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NavItem({ icon: Icon, label, active, count }) {
  return (
    <button
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
        active ? 'bg-primary-app/10 text-primary-app' : 'text-app-secondary hover:bg-surface-muted'
      }`}
    >
      <span className="flex items-center gap-2.5">
        <Icon size={16} />
        {label}
      </span>
      {count > 0 && (
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            active ? 'bg-primary-app text-white' : 'bg-surface-muted text-app-muted'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function Kpi({ label, value, tone }) {
  const tones = {
    warning: { bg: '#D4A37320', color: '#8B5E2C' },
    destructive: { bg: '#C5303015', color: '#C53030' },
    info: { bg: '#2B6CB015', color: '#2B6CB0' },
    success: { bg: '#3B7A5715', color: '#3B7A57' },
  };
  const tcfg = tones[tone] || tones.info;
  return (
    <div className="bg-surface border border-app rounded-lg p-4">
      <div
        className="inline-block text-xs font-medium px-2 py-0.5 rounded mb-2"
        style={{ background: tcfg.bg, color: tcfg.color }}
      >
        {label}
      </div>
      <div className="font-serif text-3xl text-app">{value}</div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3 ${className}`}>{children}</th>;
}
function Td({ children, className = '' }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function StatusBadge({ status, labels }) {
  const cfg = {
    abierta: { bg: '#D4A37320', color: '#8B5E2C', icon: AlertCircle },
    en_curso: { bg: '#2B6CB015', color: '#2B6CB0', icon: Clock },
    resuelta: { bg: '#3B7A5715', color: '#3B7A57', icon: CheckCircle2 },
  }[status];
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon size={11} /> {labels[status]}
    </span>
  );
}

function UrgencyBadge({ urg, labels }) {
  const cfg = {
    alta: 'text-[#C53030]',
    media: 'text-[#8B5E2C]',
    baja: 'text-app-muted',
  }[urg];
  return <span className={`text-xs font-medium ${cfg}`}>{labels[urg]}</span>;
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={15} className="text-app-muted mt-0.5 flex-shrink-0" />
      <div>
        <div className="label-eyebrow text-[10px] mb-0.5">{label}</div>
        <div className="text-sm text-app">{value}</div>
      </div>
    </div>
  );
}
